import { PaginationState } from "@tanstack/react-table"
import { useMemo } from "react"
import {
  type Query as CubeQuery,
  type TimeDimension,
  type BinaryFilter,
} from "@cubejs-client/core"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"
import useDate from "~/ui-rtk/hooks/date"
import { getPivotData } from "~/ui-rtk/utils/cube"
import { MetricsMapping } from "~/ui-rtk/constants/metrics-mapping"
import { calculateConversionRates } from "./mockData"
import { TSortingState } from "~/ui-rtk/hooks/url-sorting"

/**
 * ============================================================================
 * CONSTANTS
 * ============================================================================
 */

const PRODUCT_PAGE_FUNNEL_CUBE = "company_web_analytics_product_page_funnel"

/**
 * Valid column keys that support server-side sorting.
 */
type SortableColumnKey =
  | "productTitle"
  | "productHandle"
  | "productViewers"
  | "addersToCart"
  | "checkouts"
  | "purchasers"

/**
 * Maps UI column keys (camelCase) to Cube.js dimension/measure names (snake_case).
 *
 * Must stay in sync with the columns in ProductPageFunnelTable that are NOT
 * flagged `disableSorting` — the table renders a clickable header for those but
 * sorts nothing itself (manualSorting), so a column missing from here is a
 * header that silently does nothing. `sortable-columns.test.ts` enforces the
 * pairing in both directions.
 */
export const SORT_KEY_MAPPING: Record<SortableColumnKey, string> = {
  productTitle: "product_title",
  productHandle: "product_handle",
  productViewers: "product_viewers",
  addersToCart: "adders_to_cart",
  checkouts: "checkouts",
  purchasers: "purchasers",
} as const

/**
 * ============================================================================
 * TYPE DEFINITIONS
 * ============================================================================
 */

export type SessionFilter = "TOTAL" | "ORGANIC" | "PAID" | "TRIGGERED"

/**
 * ProductPageFunnelData interface for 4-step funnel
 */
export interface ProductPageFunnelData {
  productViewers: number
  addersToCart: number
  checkouts: number
  purchasers: number
  productToCartConversion: number
  cartToCheckoutConversion: number
  checkoutToPurchaseConversion: number
  productToPurchaseConversion: number
  productToCartAbandon: number
  cartToCheckoutAbandon: number
  checkoutToPurchaseAbandon: number
  productToPurchaseAbandon: number
  totalRevenue: number
  avgOrderValue: number
  firstOrders: number
  repeatOrders: number
  revenueAtCart: number
  revenueAtCheckout: number
  revenueAtPurchase: number
}

/**
 * ProductTableRow represents data for each product row
 */
export interface ProductTableRow {
  productHandle: string
  productTitle: string
  productId: string
  productViewers: number
  addersToCart: number
  checkouts: number
  purchasers: number
  productToCartConversion: number
  productToCartAbandon: number
  cartToCheckoutConversion: number
  cartToCheckoutAbandon: number
  checkoutToPurchaseConversion: number
  checkoutToPurchaseAbandon: number
  productToPurchaseConversion: number
  productToPurchaseAbandon: number
  firstOrders: number
  repeatOrders: number
  sumFirstOrderRevs: number
  sumSecondOrderRevs: number
  sumThirdOrderRevs: number
  sumFourthOrderRevs: number
  sumFifthPlusOrderRevs: number
  avgFirstOrderLtv90Day: number
  avgSecondOrderLtv90Day: number
  avgThirdOrderLtv90Day: number
  avgFourthOrderLtv90Day: number
  avgFifthPlusOrderLtv90Day: number
  sumFirstOrderPreMktgContribution: number
  sumSecondOrderPreMktgContribution: number
  sumThirdOrderPreMktgContribution: number
  sumFourthOrderPreMktgContribution: number
  sumFifthPlusOrderPreMktgContribution: number
  estimatedRevenue: number
  estimatedPerViewValue: number
}

/**
 * ============================================================================
 * MAIN HOOK
 * ============================================================================
 */
export default function useConnect(
  sessionFilter: SessionFilter,
  paginationState?: PaginationState,
  dateRange?: (string | undefined)[],
  sorting?: TSortingState[],
  compareRange?: (string | undefined)[],
  /** When true, only fetch summary (for funnel chart). Table uses Chart/metrics-mapping. */
  summaryOnly = false,
  additionalTableFilters?: BinaryFilter[],
) {
  const currentCompany = useCurrentCompany()
  const { dateRange: fallbackDateRange } = useMinMaxDateRange()
  const dayjs = useDate()

  const companyId = currentCompany?.id || ""
  const effectiveDateRange =
    dateRange && dateRange[0] && dateRange[1]
      ? [dateRange[0], dateRange[1]]
      : [
          dayjs(fallbackDateRange[0]).format("YYYY-MM-DD"),
          dayjs(fallbackDateRange[1]).format("YYYY-MM-DD"),
        ]
  const startDate = effectiveDateRange[0]
  const endDate = effectiveDateRange[1]

  // Handle comparison date range
  const effectiveCompareRange =
    compareRange && compareRange[0] && compareRange[1]
      ? [compareRange[0], compareRange[1]]
      : undefined
  const compareStartDate = effectiveCompareRange?.[0]
  const compareEndDate = effectiveCompareRange?.[1]
  const isComparing = Boolean(compareStartDate && compareEndDate)

  // ============================================================================
  // REAL MODE - Use metrics mapping
  // ============================================================================

  const summaryQuery = useMemo(() => {
    const baseQuery = MetricsMapping.product_page_funnel_summary.q as CubeQuery
    const additionalFilters: BinaryFilter[] = []

    additionalFilters.push({
      member: `${PRODUCT_PAGE_FUNNEL_CUBE}.companyid`,
      operator: "equals",
      values: [companyId],
    })

    additionalFilters.push({
      member: `${PRODUCT_PAGE_FUNNEL_CUBE}.session_filter`,
      operator: "equals",
      values: [sessionFilter],
    })

    const timeDimensions = baseQuery.timeDimensions?.map(
      (td: TimeDimension) => {
        if (isComparing && compareStartDate && compareEndDate) {
          return {
            ...td,
            compareDateRange: [
              [compareStartDate, compareEndDate],
              [startDate, endDate],
            ],
          }
        }
        return { ...td, dateRange: [startDate, endDate] }
      },
    )

    return {
      ...baseQuery,
      filters: [...(baseQuery.filters || []), ...additionalFilters],
      timeDimensions,
    } as CubeQuery
  }, [
    sessionFilter,
    companyId,
    startDate,
    endDate,
    isComparing,
    compareStartDate,
    compareEndDate,
  ])

  const countQuery = useMemo(() => {
    const filters: BinaryFilter[] = []

    filters.push({
      member: `${PRODUCT_PAGE_FUNNEL_CUBE}.companyid`,
      operator: "equals",
      values: [companyId],
    })

    filters.push({
      member: `${PRODUCT_PAGE_FUNNEL_CUBE}.session_filter`,
      operator: "equals",
      values: [sessionFilter],
    })

    if (additionalTableFilters?.length) {
      filters.push(...additionalTableFilters)
    }

    return {
      dimensions: [`${PRODUCT_PAGE_FUNNEL_CUBE}.product_id`],
      filters,
      timeDimensions: [
        {
          dimension: `${PRODUCT_PAGE_FUNNEL_CUBE}.md_date`,
          dateRange: [startDate, endDate],
        },
      ],
    } as CubeQuery
  }, [sessionFilter, companyId, startDate, endDate, additionalTableFilters])

  const tableQuery = useMemo(() => {
    const baseQuery = MetricsMapping.product_page_funnel_table.q as CubeQuery
    const filters: BinaryFilter[] = []

    filters.push({
      member: `${PRODUCT_PAGE_FUNNEL_CUBE}.companyid`,
      operator: "equals",
      values: [companyId],
    })

    filters.push({
      member: `${PRODUCT_PAGE_FUNNEL_CUBE}.session_filter`,
      operator: "equals",
      values: [sessionFilter],
    })

    if (additionalTableFilters?.length) {
      filters.push(...additionalTableFilters)
    }

    const timeDimensions = baseQuery.timeDimensions?.map(
      (td: TimeDimension) => {
        if (isComparing && compareStartDate && compareEndDate) {
          return {
            ...td,
            compareDateRange: [
              [compareStartDate, compareEndDate],
              [startDate, endDate],
            ],
          }
        }
        return { ...td, dateRange: [startDate, endDate] }
      },
    )

    const query = {
      ...baseQuery,
      filters: [...(baseQuery.filters || []), ...filters],
      timeDimensions,
    } as CubeQuery

    if (paginationState) {
      query.limit = paginationState.pageSize
      query.offset = paginationState.pageIndex * paginationState.pageSize
    }

    if (sorting?.[0]) {
      const sort = sorting[0]
      const mappedKey = SORT_KEY_MAPPING[sort.id as SortableColumnKey]
      if (mappedKey) {
        query.order = {
          [`${PRODUCT_PAGE_FUNNEL_CUBE}.${mappedKey}`]: sort.desc
            ? "desc"
            : "asc",
        }
      }
    }

    return query
  }, [
    sessionFilter,
    companyId,
    startDate,
    endDate,
    paginationState,
    sorting,
    isComparing,
    compareStartDate,
    compareEndDate,
    additionalTableFilters,
  ])

  // Fetch data
  const {
    resultSet: summaryResultSet,
    isLoading: isSummaryLoading,
    error: summaryError,
  } = useCubeQuery(summaryQuery)
  const {
    resultSet: tableResultSet,
    isLoading: isTableLoading,
    error: tableError,
  } = useCubeQuery(tableQuery, { skip: summaryOnly })
  const { resultSet: countResultSet, isLoading: isCountLoading } = useCubeQuery(
    countQuery,
    { skip: summaryOnly },
  )

  // Helper function to process funnel data from raw data array
  const processFunnelData = (data: Array<Record<string, unknown>>) => {
    const totals = data.reduce(
      (
        acc: {
          productViewers: number
          addersToCart: number
          checkouts: number
          purchasers: number
          firstOrders: number
          repeatOrders: number
          totalRevenue: number
        },
        row: Record<string, unknown>,
      ) => ({
        productViewers:
          acc.productViewers +
          Number(row[`${PRODUCT_PAGE_FUNNEL_CUBE}.product_viewers`] || 0),
        addersToCart:
          acc.addersToCart +
          Number(row[`${PRODUCT_PAGE_FUNNEL_CUBE}.adders_to_cart`] || 0),
        checkouts:
          acc.checkouts +
          Number(row[`${PRODUCT_PAGE_FUNNEL_CUBE}.checkouts`] || 0),
        purchasers:
          acc.purchasers +
          Number(row[`${PRODUCT_PAGE_FUNNEL_CUBE}.purchasers`] || 0),
        firstOrders:
          acc.firstOrders +
          Number(row[`${PRODUCT_PAGE_FUNNEL_CUBE}.first_orders`] || 0),
        repeatOrders:
          acc.repeatOrders +
          Number(row[`${PRODUCT_PAGE_FUNNEL_CUBE}.repeat_orders`] || 0),
        totalRevenue:
          acc.totalRevenue +
          Number(row[`${PRODUCT_PAGE_FUNNEL_CUBE}.total_revenue`] || 0),
      }),
      {
        productViewers: 0,
        addersToCart: 0,
        checkouts: 0,
        purchasers: 0,
        firstOrders: 0,
        repeatOrders: 0,
        totalRevenue: 0,
      },
    )

    const conversionRates = calculateConversionRates(
      totals.productViewers,
      totals.addersToCart,
      totals.checkouts,
      totals.purchasers,
    )

    // Calculate average order value - return 0 if no purchasers to avoid misleading metrics
    const avgOrderValue =
      totals.purchasers > 0 ? totals.totalRevenue / totals.purchasers : 0

    return {
      productViewers: totals.productViewers,
      addersToCart: totals.addersToCart,
      checkouts: totals.checkouts,
      purchasers: totals.purchasers,
      ...conversionRates,
      totalRevenue: totals.totalRevenue,
      avgOrderValue,
      firstOrders: totals.firstOrders,
      repeatOrders: totals.repeatOrders,
      revenueAtCart: totals.addersToCart * avgOrderValue,
      revenueAtCheckout: totals.checkouts * avgOrderValue,
      revenueAtPurchase: totals.totalRevenue,
    }
  }

  // Process summary data
  const funnelData = useMemo<ProductPageFunnelData | null>(() => {
    if (!summaryResultSet) return null

    // When using compareDateRange, access loadResponses
    const resultSetWithResponses = summaryResultSet as unknown as {
      loadResponses?: Array<{ data?: Array<Record<string, unknown>> }>
    }

    if (
      isComparing &&
      resultSetWithResponses.loadResponses &&
      resultSetWithResponses.loadResponses.length > 1
    ) {
      // loadResponses[1] = current period data
      const currentData = resultSetWithResponses.loadResponses[1]?.data || []
      return processFunnelData(currentData)
    }

    // Non-comparison mode - use regular pivot data
    const pivotData = getPivotData(summaryResultSet, summaryQuery)
    return processFunnelData(pivotData)
  }, [summaryResultSet, summaryQuery, isComparing])

  // Process comparison summary data (from loadResponses[0])
  const compareFunnelData = useMemo<ProductPageFunnelData | null>(() => {
    if (!isComparing || !summaryResultSet) {
      return null
    }

    // When using compareDateRange, access loadResponses
    const resultSetWithResponses = summaryResultSet as unknown as {
      loadResponses?: Array<{ data?: Array<Record<string, unknown>> }>
    }

    if (
      resultSetWithResponses.loadResponses &&
      resultSetWithResponses.loadResponses.length > 0
    ) {
      // loadResponses[0] = comparison period data
      const comparisonData = resultSetWithResponses.loadResponses[0]?.data || []
      return processFunnelData(comparisonData)
    }

    return null
  }, [isComparing, summaryResultSet])

  // Helper function to process table row data
  const processTableRow = (row: Record<string, unknown>): ProductTableRow => {
    const productViewers = Number(
      row[`${PRODUCT_PAGE_FUNNEL_CUBE}.product_viewers`] || 0,
    )
    const addersToCart = Number(
      row[`${PRODUCT_PAGE_FUNNEL_CUBE}.adders_to_cart`] || 0,
    )
    const checkouts = Number(row[`${PRODUCT_PAGE_FUNNEL_CUBE}.checkouts`] || 0)
    const purchasers = Number(
      row[`${PRODUCT_PAGE_FUNNEL_CUBE}.purchasers`] || 0,
    )
    const firstOrders = Number(
      row[`${PRODUCT_PAGE_FUNNEL_CUBE}.first_orders`] || 0,
    )
    const repeatOrders = Number(
      row[`${PRODUCT_PAGE_FUNNEL_CUBE}.repeat_orders`] || 0,
    )

    const conversionRates = calculateConversionRates(
      productViewers,
      addersToCart,
      checkouts,
      purchasers,
    )

    return {
      productHandle: String(
        row[`${PRODUCT_PAGE_FUNNEL_CUBE}.product_handle`] || "",
      ),
      productTitle: String(
        row[`${PRODUCT_PAGE_FUNNEL_CUBE}.product_title`] || "Unnamed Product",
      ),
      productId: String(row[`${PRODUCT_PAGE_FUNNEL_CUBE}.product_id`] || ""),
      productViewers,
      addersToCart,
      checkouts,
      purchasers,
      ...conversionRates,
      firstOrders,
      repeatOrders,
      sumFirstOrderRevs: Number(
        row[`${PRODUCT_PAGE_FUNNEL_CUBE}.sum_first_order_revs`] || 0,
      ),
      sumSecondOrderRevs: Number(
        row[`${PRODUCT_PAGE_FUNNEL_CUBE}.sum_second_order_revs`] || 0,
      ),
      sumThirdOrderRevs: Number(
        row[`${PRODUCT_PAGE_FUNNEL_CUBE}.sum_third_order_revs`] || 0,
      ),
      sumFourthOrderRevs: Number(
        row[`${PRODUCT_PAGE_FUNNEL_CUBE}.sum_fourth_order_revs`] || 0,
      ),
      sumFifthPlusOrderRevs: Number(
        row[`${PRODUCT_PAGE_FUNNEL_CUBE}.sum_fifth_plus_order_revs`] || 0,
      ),
      avgFirstOrderLtv90Day: Number(
        row[`${PRODUCT_PAGE_FUNNEL_CUBE}.avg_first_order_ltv_90_day`] || 0,
      ),
      avgSecondOrderLtv90Day: Number(
        row[`${PRODUCT_PAGE_FUNNEL_CUBE}.avg_second_order_ltv_90_day`] || 0,
      ),
      avgThirdOrderLtv90Day: Number(
        row[`${PRODUCT_PAGE_FUNNEL_CUBE}.avg_third_order_ltv_90_day`] || 0,
      ),
      avgFourthOrderLtv90Day: Number(
        row[`${PRODUCT_PAGE_FUNNEL_CUBE}.avg_fourth_order_ltv_90_day`] || 0,
      ),
      avgFifthPlusOrderLtv90Day: Number(
        row[`${PRODUCT_PAGE_FUNNEL_CUBE}.avg_fifth_plus_order_ltv_90_day`] || 0,
      ),
      sumFirstOrderPreMktgContribution: Number(
        row[
          `${PRODUCT_PAGE_FUNNEL_CUBE}.sum_first_order_pre_mktg_contribution`
        ] || 0,
      ),
      sumSecondOrderPreMktgContribution: Number(
        row[
          `${PRODUCT_PAGE_FUNNEL_CUBE}.sum_second_order_pre_mktg_contribution`
        ] || 0,
      ),
      sumThirdOrderPreMktgContribution: Number(
        row[
          `${PRODUCT_PAGE_FUNNEL_CUBE}.sum_third_order_pre_mktg_contribution`
        ] || 0,
      ),
      sumFourthOrderPreMktgContribution: Number(
        row[
          `${PRODUCT_PAGE_FUNNEL_CUBE}.sum_fourth_order_pre_mktg_contribution`
        ] || 0,
      ),
      sumFifthPlusOrderPreMktgContribution: Number(
        row[
          `${PRODUCT_PAGE_FUNNEL_CUBE}.sum_fifth_plus_order_pre_mktg_contribution`
        ] || 0,
      ),
      estimatedRevenue: Number(
        row[`${PRODUCT_PAGE_FUNNEL_CUBE}.estimated_revenue`] || 0,
      ),
      estimatedPerViewValue: Number(
        row[`${PRODUCT_PAGE_FUNNEL_CUBE}.estimated_per_view_value`] || 0,
      ),
    }
  }

  // Process table data
  const tableData = useMemo<ProductTableRow[]>(() => {
    if (!tableResultSet) return []

    // When using compareDateRange, access loadResponses
    const resultSetWithResponses = tableResultSet as unknown as {
      loadResponses?: Array<{ data?: Array<Record<string, unknown>> }>
    }

    if (
      isComparing &&
      resultSetWithResponses.loadResponses &&
      resultSetWithResponses.loadResponses.length > 1
    ) {
      // loadResponses[1] = current period data
      const currentData = resultSetWithResponses.loadResponses[1]?.data || []
      return currentData.map((row: Record<string, unknown>) =>
        processTableRow(row),
      )
    }

    // Non-comparison mode - use regular pivot data
    const pivotData = getPivotData(tableResultSet, tableQuery)
    return pivotData.map((row: Record<string, unknown>) => processTableRow(row))
  }, [tableResultSet, tableQuery, isComparing])

  const totalCount = useMemo(() => {
    if (!countResultSet) return 0
    const pivotData = getPivotData(countResultSet, countQuery)
    return pivotData.length
  }, [countResultSet, countQuery])

  // Process comparison table data (from loadResponses[0])
  const compareTableData = useMemo<ProductTableRow[] | undefined>(() => {
    if (!isComparing || !tableResultSet) {
      return undefined
    }

    // When using compareDateRange, access loadResponses
    const resultSetWithResponses = tableResultSet as unknown as {
      loadResponses?: Array<{ data?: Array<Record<string, unknown>> }>
    }

    if (
      resultSetWithResponses.loadResponses &&
      resultSetWithResponses.loadResponses.length > 0
    ) {
      // loadResponses[0] = comparison period data
      const comparisonData = resultSetWithResponses.loadResponses[0]?.data || []
      return comparisonData.map((row: Record<string, unknown>) =>
        processTableRow(row),
      )
    }

    return undefined
  }, [isComparing, tableResultSet])

  return {
    isLoading: isSummaryLoading || isTableLoading || isCountLoading,
    error: summaryError || tableError,
    funnelData,
    compareFunnelData,
    tableData,
    compareTableData,
    totalCount,
    /** The exact request behind `tableData` — the CSV export re-runs it as-is. */
    tableQuery,
  }
}
